import type {
  AcademicRecord,
  AdHocInvoice,
  BulkChargeJob,
  BulkChargeSegmentPreview,
  BulkAssignRecurringFeeResult,
  Communication,
  Family,
  FamilyAccountTransaction,
  FamilyBalanceSummary,
  FamilyInteraction,
  FamilyPaymentMethod,
  ParentContact,
  RecurringFeeAssignment,
  RecurringFeeDefinition,
  SchoolTuitionDashboardTree,
  SchoolTuitionFamilyLedgerTree,
  StudentsDashboardTree,
  FamilyDetailCoreTree,
  FamilyDetailDocumentsTree,
  FamilyDetailNotesTree,
  FamilyDetailTuitionTree,
  DemoRolePlayerBaselineRestoreApplyResult,
  DemoRolePlayerBaselineRestoreLivePreview,
  QaTuitionBalanceResetApplyResult,
  QaTuitionBalanceResetPreview,
  QaTuitionBalanceResetRollbackResult,
  QaDelegatedPayerFixtureApplyResult,
  QaDelegatedPayerFixturePreview,
  QaDelegatedPayerFixtureRollbackResult,
  QaPayerPaymentRunPreview,
  QaPayerPaymentRunPromotionApproval,
  QaPayerPaymentRunReceipt,
  QaPayerPaymentRunCleanupEvidence,
  QaPayerPaymentRunReceiptHashes,
  QaRealisticDemoRosterApplyResult,
  QaRealisticDemoRosterPreview,
  QaRealisticDemoRosterRollbackResult,
  Student,
  StudentDocument,
} from "./entities";

export type StudentsFamiliesConnection<TItem> = {
  items: TItem[];
  totalCount: number;
  nextToken?: string | null;
};

export type SignedUploadUrlResponse = {
  uploadUrl: string;
  storageKey: string;
  resourceId: string;
  expiresAt: number;
};

export type SignedDownloadUrlResponse = {
  downloadUrl: string;
  fileName: string;
  contentType?: string | null;
  expiresAt: number;
};

export type FamilyListResponse = StudentsFamiliesConnection<Family>;
export type FamilySearchResponse = StudentsFamiliesConnection<Family>;
export type FamilyDetailResponse = {
  family: Family;
  students: Student[];
  parentContacts: ParentContact[];
};
export type StudentDetailResponse = {
  student: Student;
  documents: StudentDocument[];
  academicRecords: AcademicRecord[];
};
export type CommunicationListResponse =
  StudentsFamiliesConnection<Communication>;
export type FamilyInteractionListResponse =
  StudentsFamiliesConnection<FamilyInteraction>;
export type FamilyAccountTransactionListResponse =
  StudentsFamiliesConnection<FamilyAccountTransaction>;
export type FamilyDocumentListResponse =
  StudentsFamiliesConnection<StudentDocument>;
export type FamilyPaymentMethodListResponse =
  StudentsFamiliesConnection<FamilyPaymentMethod>;

export const STUDENTS_FAMILIES_QUERY_OPERATION_IDS = [
  "listFamilies",
  "searchFamilies",
  "getFamily",
  "getStudent",
  "listCommunications",
  "getFamilyBalance",
  "listFamilyAccountTransactions",
  "listFamilyTimelineEvents",
  "listFamilyDocuments",
  "getFamilyDocumentDownloadUrl",
  "listFamilyPaymentMethods",
  "getSchoolTuitionDashboardTree",
  "getSchoolTuitionFamilyLedgerTree",
  "getStudentsDashboardTree",
  "getFamilyDetailCoreTree",
  "getFamilyDetailTuitionTree",
  "getFamilyDetailDocumentsTree",
  "getFamilyDetailNotesTree",
  "previewQaTuitionBalanceReset",
  "previewQaDelegatedPayerFixture",
  "previewQaStaffPayerFixture",
  "previewQaPayerPaymentRun",
  "getQaPayerPaymentRun",
  "previewQaRealisticDemoRoster",
  "previewDemoRolePlayerBaselineRestore",
  "getStaffPayerAssignmentReview",
  "getAdHocInvoice",
  "listAdHocInvoices",
  "getBulkChargeJob",
  "previewBulkChargeSegment",
  "getRecurringFeeDefinition",
  "listRecurringFeeDefinitions",
  "listRecurringFeeAssignments",
] as const;

export type StudentsFamiliesQueryOperationId =
  (typeof STUDENTS_FAMILIES_QUERY_OPERATION_IDS)[number];

export const STUDENTS_FAMILIES_MUTATION_OPERATION_IDS = [
  "createFamilyProfile",
  "createStudentProfile",
  "createFamilyEngagementEvent",
  "createFamilyBillingEntry",
  "updateFamilyWorkflowStatus",
  "sendFamilyCommunication",
  "createFamilyScheduleEvent",
  "updateFamilyScheduleEvent",
  "createFamilyNote",
  "deleteFamilyNote",
  "updateStudentProfile",
  "assignStudentCaseOwner",
  "updateFamilyProfile",
  "requestFamilyDocumentUpload",
  "createFamilyDocumentRecord",
  "updateFamilyDocumentRecord",
  "deleteFamilyDocumentRecord",
  "requestFamilyNoteAttachmentUpload",
  "createFamilyPaymentMethod",
  "updateFamilyPaymentMethod",
  "archiveAdHocTuitionCharge",
  "reverseTuitionPayment",
  "issueTuitionRefund",
  "createAdHocCharge",
  "createBulkCharge",
  "createRecurringFeeDefinition",
  "updateRecurringFeeDefinition",
  "deleteRecurringFeeDefinition",
  "assignRecurringFee",
  "unassignRecurringFee",
  "bulkAssignRecurringFee",
  "applyQaTuitionBalanceReset",
  "applyDemoRolePlayerBaselineRestore",
  "rollbackQaTuitionBalanceReset",
  "applyQaDelegatedPayerFixture",
  "rollbackQaDelegatedPayerFixture",
  "applyQaStaffPayerFixture",
  "applyQaPayerPaymentRun",
  "beginQaPayerPaymentRun",
  "completeQaPayerPaymentRun",
  "markQaPayerPaymentRunRecoveryRequired",
  "applyQaRealisticDemoRoster",
  "rollbackQaRealisticDemoRoster",
  "revokeStaffPayerAssignment",
  "repairStaffPayerAssignment",
  "sendStaffPayerInvitation",
] as const;

export type StudentsFamiliesMutationOperationId =
  (typeof STUDENTS_FAMILIES_MUTATION_OPERATION_IDS)[number];

export type StudentsFamiliesRouterJsonInput =
  | string
  | Record<string, unknown>
  | null
  | undefined;

type QaPayerPaymentRunRouterInput = {
  schoolId: string;
  tenantSlug: string;
  stage: "pilot" | "payer_modes" | "scale_10";
  releaseEvidenceId: string;
  externallyVerifiedDeployedSha: string;
  cleanupOwner: string;
  promotionApproval?: QaPayerPaymentRunPromotionApproval;
};

export type StudentsFamiliesQueryArgsMap = {
  listFamilies: { input?: StudentsFamiliesRouterJsonInput };
  searchFamilies: { input?: StudentsFamiliesRouterJsonInput };
  getFamily: { familyId: string };
  getStudent: { studentId: string };
  listCommunications: { familyId: string; limit?: number };
  getFamilyBalance: { familyId: string; academicYear: string };
  listFamilyAccountTransactions: {
    familyId: string;
    academicYear?: string;
    limit?: number;
    nextToken?: string;
  };
  listFamilyTimelineEvents: {
    familyId: string;
    limit?: number;
    nextToken?: string;
  };
  listFamilyDocuments: {
    familyId: string;
    limit?: number;
    nextToken?: string;
  };
  getFamilyDocumentDownloadUrl: { documentId: string };
  listFamilyPaymentMethods: {
    familyId: string;
    limit?: number;
    nextToken?: string;
  };
  getSchoolTuitionDashboardTree: {
    academicYear: string;
    limit?: number;
    offset?: number;
    searchQuery?: string;
  };
  getSchoolTuitionFamilyLedgerTree: {
    academicYear: string;
    familyId: string;
  };
  getStudentsDashboardTree: {
    limit?: number;
    offset?: number;
  };
  getFamilyDetailCoreTree: {
    familyId: string;
  };
  getFamilyDetailTuitionTree: {
    familyId: string;
    academicYear: string;
    limit?: number;
    nextToken?: string | null;
  };
  getFamilyDetailDocumentsTree: {
    familyId: string;
    limit?: number;
    nextToken?: string | null;
  };
  getFamilyDetailNotesTree: {
    familyId: string;
    limit?: number;
    nextToken?: string | null;
  };
  previewQaTuitionBalanceReset: {
    input: {
      schoolId: string;
      tenantSlug: string;
      familyId: string;
      academicYear: string;
      targetOpenBalanceCents: number;
    };
  };
  previewQaDelegatedPayerFixture: {
    input: {
      schoolId: string;
      tenantSlug: string;
      releaseEvidenceId: string;
      externallyVerifiedDeployedSha: string;
      cleanupOwner: string;
    };
  };
  previewQaStaffPayerFixture: {
    input: {
      schoolId: string;
      tenantSlug: string;
      releaseEvidenceId: string;
      externallyVerifiedDeployedSha: string;
      cleanupOwner: string;
    };
  };
  previewQaPayerPaymentRun: { input: QaPayerPaymentRunRouterInput };
  getQaPayerPaymentRun: {
    input: { schoolId: string; tenantSlug: string; runId: string };
  };
  previewQaRealisticDemoRoster: {
    input: {
      schoolId: string;
      tenantSlug: string;
      releaseEvidenceId: string;
      externallyVerifiedDeployedSha: string;
      cleanupOwner: string;
    };
  };
  previewDemoRolePlayerBaselineRestore: {
    input: {
      schoolId: string;
      tenantSlug: string;
      rolePlayerKey: string;
      scenarioId: string;
    };
  };
  getStaffPayerAssignmentReview: {
    input: {
      schoolId: string;
      tenantSlug: string;
      familyId: string;
      academicYear: string;
    };
  };
  getAdHocInvoice: {
    invoiceId: string;
  };
  listAdHocInvoices: {
    tuitionAccountId: string;
    limit?: number;
    nextToken?: string;
  };
  getBulkChargeJob: {
    bulkChargeJobId: string;
  };
  previewBulkChargeSegment: {
    grades?: string[];
    divisions?: string[];
    gender?: "ALL" | "BOYS" | "GIRLS";
    rateCategories?: string[];
  };
  getRecurringFeeDefinition: {
    recurringFeeId: string;
  };
  listRecurringFeeDefinitions: {
    schoolId?: string;
    isActive?: boolean;
  };
  listRecurringFeeAssignments: {
    tuitionAccountId?: string;
    studentId?: string;
    recurringFeeId?: string;
    isActive?: boolean;
    limit?: number;
    nextToken?: string;
  };
};

export type StudentsFamiliesQueryResultMap = {
  listFamilies: FamilyListResponse;
  searchFamilies: FamilySearchResponse;
  getFamily: FamilyDetailResponse;
  getStudent: StudentDetailResponse;
  listCommunications: CommunicationListResponse;
  getFamilyBalance: FamilyBalanceSummary;
  listFamilyAccountTransactions: FamilyAccountTransactionListResponse;
  listFamilyTimelineEvents: FamilyInteractionListResponse;
  listFamilyDocuments: FamilyDocumentListResponse;
  getFamilyDocumentDownloadUrl: SignedDownloadUrlResponse;
  listFamilyPaymentMethods: FamilyPaymentMethodListResponse;
  getSchoolTuitionDashboardTree: SchoolTuitionDashboardTree;
  getSchoolTuitionFamilyLedgerTree: SchoolTuitionFamilyLedgerTree;
  getStudentsDashboardTree: StudentsDashboardTree;
  getFamilyDetailCoreTree: FamilyDetailCoreTree;
  getFamilyDetailTuitionTree: FamilyDetailTuitionTree;
  getFamilyDetailDocumentsTree: FamilyDetailDocumentsTree;
  getFamilyDetailNotesTree: FamilyDetailNotesTree;
  previewQaTuitionBalanceReset: QaTuitionBalanceResetPreview;
  previewQaDelegatedPayerFixture: QaDelegatedPayerFixturePreview;
  previewQaStaffPayerFixture: QaDelegatedPayerFixturePreview;
  previewQaPayerPaymentRun: QaPayerPaymentRunPreview;
  getQaPayerPaymentRun: QaPayerPaymentRunReceipt;
  previewQaRealisticDemoRoster: QaRealisticDemoRosterPreview;
  previewDemoRolePlayerBaselineRestore: DemoRolePlayerBaselineRestoreLivePreview;
  getStaffPayerAssignmentReview: Record<string, unknown>;
  getAdHocInvoice: AdHocInvoice;
  listAdHocInvoices: StudentsFamiliesConnection<AdHocInvoice>;
  getBulkChargeJob: BulkChargeJob;
  previewBulkChargeSegment: BulkChargeSegmentPreview;
  getRecurringFeeDefinition: RecurringFeeDefinition;
  listRecurringFeeDefinitions: StudentsFamiliesConnection<RecurringFeeDefinition>;
  listRecurringFeeAssignments: StudentsFamiliesConnection<RecurringFeeAssignment>;
};

export type StudentsFamiliesMutationArgsMap = {
  createFamilyProfile: { input: StudentsFamiliesRouterJsonInput };
  createStudentProfile: { input: StudentsFamiliesRouterJsonInput };
  createFamilyEngagementEvent: { input: StudentsFamiliesRouterJsonInput };
  createFamilyBillingEntry: { input: StudentsFamiliesRouterJsonInput };
  updateFamilyWorkflowStatus: { input: StudentsFamiliesRouterJsonInput };
  sendFamilyCommunication: { input: StudentsFamiliesRouterJsonInput };
  createFamilyScheduleEvent: { input: StudentsFamiliesRouterJsonInput };
  updateFamilyScheduleEvent: { input: StudentsFamiliesRouterJsonInput };
  createFamilyNote: { input: StudentsFamiliesRouterJsonInput };
  deleteFamilyNote: { input: StudentsFamiliesRouterJsonInput };
  updateStudentProfile: { input: StudentsFamiliesRouterJsonInput };
  assignStudentCaseOwner: { input: StudentsFamiliesRouterJsonInput };
  updateFamilyProfile: { input: StudentsFamiliesRouterJsonInput };
  requestFamilyDocumentUpload: { input: StudentsFamiliesRouterJsonInput };
  createFamilyDocumentRecord: { input: StudentsFamiliesRouterJsonInput };
  updateFamilyDocumentRecord: { input: StudentsFamiliesRouterJsonInput };
  deleteFamilyDocumentRecord: { input: StudentsFamiliesRouterJsonInput };
  requestFamilyNoteAttachmentUpload: { input: StudentsFamiliesRouterJsonInput };
  createFamilyPaymentMethod: { input: StudentsFamiliesRouterJsonInput };
  updateFamilyPaymentMethod: { input: StudentsFamiliesRouterJsonInput };
  archiveAdHocTuitionCharge: {
    input: {
      ledgerEntryId: string;
      reason: string;
      tuitionAccountId: string;
    };
  };
  reverseTuitionPayment: {
    input: {
      ledgerEntryId: string;
      reason: string;
      tuitionAccountId: string;
    };
  };
  issueTuitionRefund: {
    input: {
      tuitionAccountId: string;
      amount: number;
      reason: "WITHDRAWAL" | "OVERPAYMENT" | "BILLING_ERROR" | "OTHER";
      reasonNotes?: string | null;
      method: "STRIPE_ORIGINAL" | "CHECK" | "OTHER";
      originalPaymentEntryId?: string | null;
      referenceNumber?: string | null;
    };
  };
  createAdHocCharge: {
    input: {
      tuitionAccountId: string;
      chargeName: string;
      amount: number;
      perStudent: boolean;
      studentIds?: string[] | null;
      dueDate: string;
      billingCategory:
        | "tuition"
        | "fees"
        | "activities"
        | "transportation"
        | "food_service"
        | "other";
      notes?: string | null;
    };
  };
  createBulkCharge: {
    input: {
      chargeName: string;
      amountPerStudent: number;
      dueDate: string;
      billingCategory:
        | "tuition"
        | "fees"
        | "activities"
        | "transportation"
        | "food_service"
        | "other";
      notes?: string | null;
      segmentFilters: {
        grades?: string[];
        divisions?: string[];
        gender?: "ALL" | "BOYS" | "GIRLS";
        rateCategories?: string[];
      };
    };
  };
  createRecurringFeeDefinition: {
    input: {
      schoolId?: string;
      name: string;
      monthlyAmount: number;
      billingCategory:
        | "activities"
        | "transportation"
        | "food_service"
        | "other";
      billingDay: number;
      startMonth: number;
      endMonth: number;
      eligibleGrades?: string[] | null;
      prorateFirstMonth?: boolean;
      isActive?: boolean;
    };
  };
  updateRecurringFeeDefinition: {
    input: {
      recurringFeeId: string;
      schoolId?: string;
      name?: string;
      monthlyAmount?: number;
      billingCategory?:
        | "activities"
        | "transportation"
        | "food_service"
        | "other";
      billingDay?: number;
      startMonth?: number;
      endMonth?: number;
      eligibleGrades?: string[] | null;
      prorateFirstMonth?: boolean;
      isActive?: boolean;
    };
  };
  deleteRecurringFeeDefinition: {
    input: {
      recurringFeeId: string;
      schoolId?: string;
    };
  };
  assignRecurringFee: {
    input: {
      recurringFeeId: string;
      studentId: string;
      tuitionAccountId: string;
      customAmount?: number | null;
      startDate?: string | null;
    };
  };
  unassignRecurringFee: {
    input: {
      assignmentId: string;
    };
  };
  bulkAssignRecurringFee: {
    input: {
      recurringFeeId: string;
      studentIds: string[];
      customAmount?: number | null;
      startDate?: string | null;
    };
  };
  applyQaTuitionBalanceReset: {
    input: {
      schoolId: string;
      tenantSlug: string;
      familyId: string;
      academicYear: string;
      targetOpenBalanceCents: number;
      expectedBeforeStateDigest: string;
      confirmationText: string;
      idempotencyKey: string;
    };
  };
  applyDemoRolePlayerBaselineRestore: {
    input: {
      schoolId: string;
      tenantSlug: string;
      rolePlayerKey: string;
      scenarioId: string;
      expectedBeforeStateDigest: string;
      confirmationText: string;
    };
  };
  rollbackQaTuitionBalanceReset: {
    input: {
      schoolId: string;
      tenantSlug: string;
      familyId: string;
      academicYear: string;
      rollbackToken: string;
      expectedStateDigest: string;
      confirmationText: string;
    };
  };
  applyQaDelegatedPayerFixture: {
    input: {
      schoolId: string;
      tenantSlug: string;
      releaseEvidenceId: string;
      externallyVerifiedDeployedSha: string;
      cleanupOwner: string;
      expectedBeforeStateDigest: string;
      confirmationText: string;
    };
  };
  rollbackQaDelegatedPayerFixture: {
    input: {
      schoolId: string;
      tenantSlug: string;
      releaseEvidenceId: string;
      externallyVerifiedDeployedSha: string;
      rollbackToken: string;
      expectedStateDigest: string;
      confirmationText: string;
    };
  };
  applyQaStaffPayerFixture: {
    input: {
      schoolId: string;
      tenantSlug: string;
      releaseEvidenceId: string;
      externallyVerifiedDeployedSha: string;
      cleanupOwner: string;
      expectedBeforeStateDigest: string;
      confirmationText: string;
    };
  };
  applyQaPayerPaymentRun: {
    input: QaPayerPaymentRunRouterInput & {
      runId: string;
      expectedBeforeStateDigest: string;
      confirmationText: string;
    };
  };
  beginQaPayerPaymentRun: {
    input: {
      schoolId: string;
      tenantSlug: string;
      runId: string;
      expectedRunDigest: string;
    };
  };
  completeQaPayerPaymentRun: {
    input: {
      schoolId: string;
      tenantSlug: string;
      runId: string;
      expectedRunDigest: string;
      cleanupEvidence: QaPayerPaymentRunCleanupEvidence[];
    };
  };
  markQaPayerPaymentRunRecoveryRequired: {
    input: {
      schoolId: string;
      tenantSlug: string;
      runId: string;
      expectedRunDigest: string;
      receiptHashes?: QaPayerPaymentRunReceiptHashes;
    };
  };
  applyQaRealisticDemoRoster: {
    input: {
      schoolId: string;
      tenantSlug: string;
      releaseEvidenceId: string;
      externallyVerifiedDeployedSha: string;
      cleanupOwner: string;
      expectedBeforeStateDigest: string;
      confirmationText: string;
    };
  };
  rollbackQaRealisticDemoRoster: {
    input: {
      schoolId: string;
      tenantSlug: string;
      releaseEvidenceId: string;
      externallyVerifiedDeployedSha: string;
      rollbackToken: string;
      expectedStateDigest: string;
      confirmationText: string;
    };
  };
  revokeStaffPayerAssignment: {
    input: {
      schoolId: string;
      tenantSlug: string;
      familyId: string;
      academicYear: string;
      payerPartyId: string;
      payerAssignmentId: string;
      reason: string;
      reviewedAssignment: {
        status: string;
        version: number;
        updatedAt: string;
        expiresAt: string | null;
        finality: "active" | "terminal";
      };
    };
  };
  repairStaffPayerAssignment: {
    input: {
      schoolId: string;
      tenantSlug: string;
      familyId: string;
      academicYear: string;
      replaceAssignmentId: string;
      payer: { email: string; displayName: string; phone?: string };
      relationship: string;
      allocation: Record<string, unknown>;
      expiresAt?: string | null;
      reason: string;
      reviewedAssignment: {
        status: string;
        version: number;
        updatedAt: string;
        expiresAt: string | null;
        finality: "active" | "terminal";
      };
    };
  };
  sendStaffPayerInvitation: {
    input: {
      schoolId: string;
      tenantSlug: string;
      familyId: string;
      academicYear: string;
      payerPartyId: string;
      payerAssignmentId: string;
      deliveryRequestId: string;
      reviewedAssignment: {
        payerPartyId: string;
        status: string;
        version: number;
        updatedAt: string;
        expiresAt: string | null;
        finality: "active" | "terminal";
      };
    };
  };
};

export type StudentsFamiliesMutationResultMap = {
  createFamilyProfile: Family;
  createStudentProfile: Student;
  createFamilyEngagementEvent: FamilyInteraction;
  createFamilyBillingEntry: FamilyAccountTransaction;
  updateFamilyWorkflowStatus: Family;
  sendFamilyCommunication: Communication;
  createFamilyScheduleEvent: FamilyInteraction;
  updateFamilyScheduleEvent: FamilyInteraction;
  createFamilyNote: FamilyInteraction;
  deleteFamilyNote: FamilyInteraction;
  updateStudentProfile: Student;
  assignStudentCaseOwner: Student;
  updateFamilyProfile: Family;
  requestFamilyDocumentUpload: SignedUploadUrlResponse;
  createFamilyDocumentRecord: StudentDocument;
  updateFamilyDocumentRecord: StudentDocument;
  deleteFamilyDocumentRecord: StudentDocument;
  requestFamilyNoteAttachmentUpload: SignedUploadUrlResponse;
  createFamilyPaymentMethod: FamilyPaymentMethod;
  updateFamilyPaymentMethod: FamilyPaymentMethod;
  archiveAdHocTuitionCharge: FamilyAccountTransaction;
  reverseTuitionPayment: FamilyAccountTransaction;
  issueTuitionRefund: FamilyAccountTransaction;
  createAdHocCharge: StudentsFamiliesConnection<AdHocInvoice>;
  createBulkCharge: BulkChargeJob;
  createRecurringFeeDefinition: RecurringFeeDefinition;
  updateRecurringFeeDefinition: RecurringFeeDefinition;
  deleteRecurringFeeDefinition: RecurringFeeDefinition;
  assignRecurringFee: RecurringFeeAssignment;
  unassignRecurringFee: RecurringFeeAssignment;
  bulkAssignRecurringFee: BulkAssignRecurringFeeResult;
  applyQaTuitionBalanceReset: QaTuitionBalanceResetApplyResult;
  applyDemoRolePlayerBaselineRestore: DemoRolePlayerBaselineRestoreApplyResult;
  rollbackQaTuitionBalanceReset: QaTuitionBalanceResetRollbackResult;
  applyQaDelegatedPayerFixture: QaDelegatedPayerFixtureApplyResult;
  rollbackQaDelegatedPayerFixture: QaDelegatedPayerFixtureRollbackResult;
  applyQaStaffPayerFixture: QaDelegatedPayerFixtureApplyResult;
  applyQaPayerPaymentRun: QaPayerPaymentRunReceipt;
  beginQaPayerPaymentRun: QaPayerPaymentRunReceipt;
  completeQaPayerPaymentRun: QaPayerPaymentRunReceipt;
  markQaPayerPaymentRunRecoveryRequired: QaPayerPaymentRunReceipt;
  applyQaRealisticDemoRoster: QaRealisticDemoRosterApplyResult;
  rollbackQaRealisticDemoRoster: QaRealisticDemoRosterRollbackResult;
  revokeStaffPayerAssignment: Record<string, unknown>;
  repairStaffPayerAssignment: Record<string, unknown>;
  sendStaffPayerInvitation: Record<string, unknown>;
};
